import { useEffect, useState, useRef } from 'react';
import { API_URL } from '../config';
import { apiFetch } from '../utils/api';
import { 
    Users, Monitor, Smartphone, Tablet, Clock, MapPin, 
    RefreshCw, XCircle, Activity, Chrome, Globe, Apple,
    Wifi, WifiOff, BarChart3
} from 'lucide-react';

export default function ActiveUsersPage() {
    const [sessions, setSessions] = useState([]);
    const [stats, setStats] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('active'); // 'active', 'history', 'stats'
    const [autoRefresh, setAutoRefresh] = useState(true);
    const intervalRef = useRef(null);

    useEffect(() => {
        fetchData();
        
        if (autoRefresh) {
            intervalRef.current = setInterval(fetchData, 30000); // Refresh every 30s
        }
        
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [autoRefresh]);

    const fetchData = async () => {
        try {
            const [sessionsRes, statsRes] = await Promise.all([
                apiFetch(`${API_URL}/api/sessions/active`),
                apiFetch(`${API_URL}/api/sessions/stats`)
            ]);
            
            if (sessionsRes.ok) setSessions(await sessionsRes.json());
            if (statsRes.ok) setStats(await statsRes.json());
        } catch (err) {
            console.error('Error fetching sessions:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        try {
            const res = await apiFetch(`${API_URL}/api/sessions/history?days=7`);
            if (res.ok) setHistory(await res.json());
        } catch (err) {
            console.error('Error fetching history:', err);
        }
    };

    const terminateSession = async (sessionId) => {
        if (!confirm('¿Terminar esta sesión? El usuario será desconectado.')) return;
        
        try {
            const res = await apiFetch(`${API_URL}/api/sessions/${sessionId}`, { method: 'DELETE' });
            if (res.ok) {
                fetchData();
            }
        } catch (err) {
            console.error('Error terminating session:', err);
        }
    };

    const getDeviceIcon = (type) => {
        switch (type?.toLowerCase()) {
            case 'móvil': return <Smartphone className="text-blue-500" size={20} />;
            case 'tablet': return <Tablet className="text-purple-500" size={20} />;
            default: return <Monitor className="text-gray-600" size={20} />;
        }
    };

    const getBrowserIcon = (browser) => {
        switch (browser?.toLowerCase()) {
            case 'chrome': return <Chrome className="text-green-500" size={16} />;
            case 'safari': return <Apple className="text-gray-500" size={16} />;
            default: return <Globe className="text-blue-500" size={16} />;
        }
    };

    const formatTimeAgo = (seconds) => {
        if (!seconds || seconds < 60) return 'Ahora';
        if (seconds < 3600) return `Hace ${Math.floor(seconds / 60)} min`;
        if (seconds < 86400) return `Hace ${Math.floor(seconds / 3600)} hrs`;
        return `Hace ${Math.floor(seconds / 86400)} días`;
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('es-CL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getActivityStatus = (seconds) => {
        if (!seconds || seconds < 300) return { label: 'Activo', color: 'bg-green-500' };
        if (seconds < 900) return { label: 'Inactivo', color: 'bg-yellow-500' };
        return { label: 'Ausente', color: 'bg-red-500' };
    };

    return (
        <div className="page p-6">
            {/* Header */}
            <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Users className="text-indigo-600" size={28} />
                        Usuarios Activos
                    </h1>
                    <p className="text-gray-500 mt-1">
                        Monitorea quién está conectado al sistema
                    </p>
                </div>
                <div className="flex gap-2 items-center">
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={autoRefresh}
                            onChange={(e) => setAutoRefresh(e.target.checked)}
                            className="rounded"
                        />
                        Auto-actualizar
                    </label>
                    <button 
                        onClick={fetchData}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-gray-900 rounded-lg hover:bg-indigo-700"
                    >
                        <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                        Actualizar
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg shadow-sm border p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-green-100 rounded-full">
                                <Wifi className="text-green-600" size={24} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-green-600">{stats.activeNow}</p>
                                <p className="text-sm text-gray-500">Conectados ahora</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm border p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-blue-100 rounded-full">
                                <Activity className="text-blue-600" size={24} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-blue-600">{stats.sessionsToday}</p>
                                <p className="text-sm text-gray-500">Sesiones hoy</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm border p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-purple-100 rounded-full">
                                <Users className="text-purple-600" size={24} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-purple-600">{stats.uniqueUsersToday}</p>
                                <p className="text-sm text-gray-500">Usuarios únicos hoy</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm border p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-orange-100 rounded-full">
                                <BarChart3 className="text-orange-600" size={24} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-orange-600">{sessions.length}</p>
                                <p className="text-sm text-gray-500">Sesiones activas</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm border mb-6">
                <div className="flex border-b">
                    <button
                        onClick={() => setTab('active')}
                        className={`px-6 py-3 font-medium ${tab === 'active' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Sesiones Activas ({sessions.length})
                    </button>
                    <button
                        onClick={() => { setTab('history'); fetchHistory(); }}
                        className={`px-6 py-3 font-medium ${tab === 'history' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Historial
                    </button>
                    <button
                        onClick={() => setTab('stats')}
                        className={`px-6 py-3 font-medium ${tab === 'stats' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Estadísticas
                    </button>
                </div>

                <div className="p-4">
                    {/* Active Sessions Tab */}
                    {tab === 'active' && (
                        <div className="space-y-3">
                            {loading ? (
                                <div className="text-center py-12 text-gray-500">Cargando...</div>
                            ) : sessions.length === 0 ? (
                                <div className="text-center py-12">
                                    <WifiOff size={48} className="mx-auto text-gray-300 mb-4" />
                                    <p className="text-gray-500">No hay sesiones activas</p>
                                </div>
                            ) : (
                                sessions.map(session => {
                                    const status = getActivityStatus(session.seconds_inactive);
                                    return (
                                        <div key={session.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border hover:shadow-sm transition-shadow">
                                            <div className="flex items-center gap-4">
                                                {/* Device Icon */}
                                                <div className="p-2 bg-white rounded-lg shadow-sm">
                                                    {getDeviceIcon(session.device_type)}
                                                </div>
                                                
                                                {/* User Info */}
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-semibold text-gray-800">{session.user_name}</span>
                                                        <span className={`w-2 h-2 rounded-full ${status.color}`}></span>
                                                        <span className="text-xs text-gray-500">{status.label}</span>
                                                    </div>
                                                    <div className="text-sm text-gray-500">{session.user_email}</div>
                                                    {session.rol_nombre && (
                                                        <span className="inline-block mt-1 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                                                            {session.rol_nombre}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Connection Info */}
                                            <div className="flex items-center gap-6">
                                                <div className="text-sm text-gray-600">
                                                    <div className="flex items-center gap-1">
                                                        {getBrowserIcon(session.browser)}
                                                        <span>{session.browser}</span>
                                                        <span className="text-gray-400">/ {session.os}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                                                        <MapPin size={12} />
                                                        {session.ip_address}
                                                    </div>
                                                </div>

                                                <div className="text-sm text-gray-600 text-right">
                                                    <div className="flex items-center gap-1">
                                                        <Clock size={14} />
                                                        <span>{formatTimeAgo(session.seconds_inactive)}</span>
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                        Login: {formatDateTime(session.login_at)}
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={() => terminateSession(session.id)}
                                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Terminar sesión"
                                                >
                                                    <XCircle size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}

                    {/* History Tab */}
                    {tab === 'history' && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="px-4 py-2 text-left">Usuario</th>
                                        <th className="px-4 py-2 text-left">Dispositivo</th>
                                        <th className="px-4 py-2 text-left">IP</th>
                                        <th className="px-4 py-2 text-left">Login</th>
                                        <th className="px-4 py-2 text-left">Última actividad</th>
                                        <th className="px-4 py-2 text-center">Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map(h => (
                                        <tr key={h.id} className="border-t hover:bg-gray-50">
                                            <td className="px-4 py-2">
                                                <div className="font-medium">{h.user_name}</div>
                                                <div className="text-xs text-gray-500">{h.user_email}</div>
                                            </td>
                                            <td className="px-4 py-2">
                                                <div className="flex items-center gap-2">
                                                    {getDeviceIcon(h.device_type)}
                                                    <span>{h.browser} / {h.os}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-2 font-mono text-xs">{h.ip_address}</td>
                                            <td className="px-4 py-2">{formatDateTime(h.login_at)}</td>
                                            <td className="px-4 py-2">{formatDateTime(h.last_activity)}</td>
                                            <td className="px-4 py-2 text-center">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${h.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                                    {h.is_active ? 'Activa' : 'Cerrada'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {history.length === 0 && (
                                <div className="text-center py-8 text-gray-500">No hay historial</div>
                            )}
                        </div>
                    )}

                    {/* Stats Tab */}
                    {tab === 'stats' && stats && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* By Device */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="font-semibold mb-4 flex items-center gap-2">
                                    <Monitor size={18} />
                                    Por Dispositivo (últimos 7 días)
                                </h3>
                                <div className="space-y-3">
                                    {stats.byDevice?.map(d => (
                                        <div key={d.device_type} className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                {getDeviceIcon(d.device_type)}
                                                <span>{d.device_type || 'Desconocido'}</span>
                                            </div>
                                            <span className="font-semibold">{d.count}</span>
                                        </div>
                                    ))}
                                    {(!stats.byDevice || stats.byDevice.length === 0) && (
                                        <p className="text-gray-500 text-sm">Sin datos</p>
                                    )}
                                </div>
                            </div>

                            {/* By Browser */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="font-semibold mb-4 flex items-center gap-2">
                                    <Globe size={18} />
                                    Por Navegador (últimos 7 días)
                                </h3>
                                <div className="space-y-3">
                                    {stats.byBrowser?.map(b => (
                                        <div key={b.browser} className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                {getBrowserIcon(b.browser)}
                                                <span>{b.browser || 'Desconocido'}</span>
                                            </div>
                                            <span className="font-semibold">{b.count}</span>
                                        </div>
                                    ))}
                                    {(!stats.byBrowser || stats.byBrowser.length === 0) && (
                                        <p className="text-gray-500 text-sm">Sin datos</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
